import { useEffect, useState } from 'react';
import type { ProductsTemplateCsvType } from '@tiendanube/common';
import { FEATURE_MONTHLY_CATALOG_EXPORT } from 'App/features';
import { CATALOG_CSV_METAFIELDS } from 'App/featuresFlags';
import { useHasTags } from 'App/hooks';
import { ExportCard } from 'commons/components';
import { useModal, useStorage, useToastStatus } from 'commons/hooks';
import { CATALOG_EXPORT_PRODUCTS } from 'config/upsellFlowSources';
import { useUpsellFlow } from 'domains/Billing/UpsellFlow/hooks';
import { useTranslationCatalog } from 'domains/Catalog/hooks';
import { ProductFiltersKeys } from 'domains/Catalog/Products/productsServices/types';
import ExportProductsAlert from './ExportProductsAlert';
import IncompatibleFiltersAlert from './IncompatibleFiltersAlert/IncompatibleFiltersAlert';
import { useProductsFilters } from '../../../hooks';
import { useExportProducts } from '../../hooks';
import useDonwloadProductCsv from '../../hooks/useDonwloadProductCsv';
import ExportProductsFiltersModal from '../ExportProductsFiltersModal';

const mapTemplateFiltersCompatibles: Record<
  ProductsTemplateCsvType,
  ProductFiltersKeys[]
> = {
  standard: [
    'sortBy',
    'categoryId',
    'published',
    'freeShipping',
    'stock',
    'minStock',
    'maxStock',
    'promotionalPrice',
    'weightDimension',
  ],
  metafield_products: ['categoryId'],
  metafield_variants: ['categoryId'],
};

function ExportProductsCard(): JSX.Element {
  const t = useTranslationCatalog('products.importExportPage.export');
  const [hasCsvMetafieldsEnabled] = useHasTags([CATALOG_CSV_METAFIELDS]);
  const [_, setExportProductsTemplate] = useStorage<ProductsTemplateCsvType>(
    'export-products-template',
    'standard',
  );
  const [showFiltersModal, openFiltersModal, closeFiltersModal] = useModal();
  const {
    filters,
    totalResults,
    isLoading,
    getTotalProducts,
    refreshTotalProducts,
    removeFilter,
  } = useExportProducts();
  const { getFilterChips } = useProductsFilters(filters);
  const {
    exportProducts,
    isExporting,
    jobCsvDownloadStatus,
    ressetExportingStatus,
    csvDownloadLinkUrl,
    exportProductsStatus,
  } = useDonwloadProductCsv();
  const [includeDescriptions, setIncludeDescriptions] = useState(false);
  const [template, setTemplate] = useState<ProductsTemplateCsvType>('standard');

  useEffect(() => {
    refreshTotalProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOnConfirm = useUpsellFlow({
    title: t('upsellTitle'),
    featureKey: FEATURE_MONTHLY_CATALOG_EXPORT,
    trackingSource: CATALOG_EXPORT_PRODUCTS,
    callback: () => {
      ressetExportingStatus();
      exportProducts(includeDescriptions, template);
      setExportProductsTemplate(template);
    },
  });

  const selectedFilters = Object.entries(filters).filter(
    ([, value]) => value !== '',
  );

  const selectedFiltersKeys = selectedFilters.map(
    ([key]) => key as ProductFiltersKeys,
  );

  const filterCompatible = mapTemplateFiltersCompatibles[template];

  const areAllFiltersCompatibles = selectedFiltersKeys.every((filter) =>
    filterCompatible.includes(filter),
  );

  const disableExport =
    totalResults === 0 || isLoading || !areAllFiltersCompatibles;

  useToastStatus({
    error: t('exportError'),
    status: exportProductsStatus,
  });

  return (
    <>
      <ExportCard
        filters={getFilterChips()}
        totalLabel={t('total')}
        total={totalResults}
        loadingTotal={isLoading}
        checkboxLabel={t('includeDescriptions')}
        checkboxValue={
          template === 'standard' ? includeDescriptions : undefined
        }
        template={hasCsvMetafieldsEnabled ? template : undefined}
        exporting={isExporting}
        disabled={disableExport}
        alert={
          <>
            <ExportProductsAlert
              exportingStatus={jobCsvDownloadStatus}
              csvUrl={csvDownloadLinkUrl}
              ressetExportingStatus={ressetExportingStatus}
            />
            <IncompatibleFiltersAlert
              show={!areAllFiltersCompatibles}
              template={template}
            />
          </>
        }
        onRemoveFilter={removeFilter}
        onOpenFilters={openFiltersModal}
        onCheckboxChange={setIncludeDescriptions}
        onChangeTemplate={setTemplate}
        onConfirm={handleOnConfirm}
      />
      <ExportProductsFiltersModal
        show={showFiltersModal}
        filters={filters}
        onConfirm={getTotalProducts}
        onClose={closeFiltersModal}
      />
    </>
  );
}

export default ExportProductsCard;
